import { useCallback, useEffect, useState } from "react";
import { useSetProduct } from "../product";
import { newFailures, onlyKnown } from "../components/knownProblems";
import { campaignBuildName } from "../components/campaignBuild";
import { useTranslation } from "react-i18next";
import { trailCrumbs } from "../components/trail";
import RunAboutView from "../components/RunAbout";
import { Link, useParams } from "react-router-dom";
import { useTitle } from "../useTitle";
import { ApiError, ago, api, atLeast, type Campaign, type CampaignStop, type Role } from "../api";
import { Counts, Loading, Notice, PageHead, Section, Status, type Count, type Tone } from "../components/Look";

const ACTIVE = ["queued", "claimed", "running"];

/**
 * A campaign drawn as a route of its runs (clear-screens stop D, piece 4).
 *
 * The page answers first - which run of how many is on the bench, or whether
 * everything planned ran and nothing failed - then shows the route: one stop
 * per profile, in the order the bench runs them, with planned against ran at
 * each. A skipped case tested nothing, so a stop with one is not green, in
 * words as well as colour.
 *
 * Drawn with the shared parts (components/Look.tsx) since 2026-09-16: the
 * first page moved onto them.
 */
export default function CampaignPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const cid = Number(id);
  const [c, setC] = useState<Campaign | null>(null);
  const [role, setRole] = useState<Role | null>(null);
  const [projectId, setProjectId] = useState<number | null>(null);
  useSetProduct(projectId);
  const [error, setError] = useState("");
  const [missing, setMissing] = useState(false);
  const [note, setNote] = useState("");
  useTitle(c ? t("campaign.title", { bench: c.bench_name ?? c.bench_id }) : t("campaign.loading"));

  const load = useCallback(async () => {
    try {
      setC(await api.campaign(cid));
      setError("");
    } catch (e) {
      setMissing(e instanceof ApiError && e.status === 404);
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }, [cid]);

  useEffect(() => {
    void load();
  }, [load]);

  // Polled only while something is still to happen; a finished campaign is a
  // record and does not change under the reader.
  useEffect(() => {
    if (c?.status !== "running") return;
    const h = setInterval(() => void load(), 10000);
    return () => clearInterval(h);
  }, [c?.status, load]);

  // The role decides whether the page offers anything that moves the bench.
  const benchId = c?.bench_id;
  useEffect(() => {
    if (benchId === undefined) return;
    let alive = true;
    api
      .bench(benchId)
      .then((d) => {
        if (!alive) return;
        setRole(d.bench.role ?? null);
        setProjectId(d.bench.project_id ?? null);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [benchId]);

  if (!c) {
    // A campaign that does not exist is not an error of the page: say so, and
    // where campaigns are, instead of a red box with the server's words.
    if (missing) {
      return (
        <PageHead back={{ to: "/now", label: `← ${t("nav.now")}` }} title={`#${cid}`}
                  answer={t("campaign.notFound", { id: cid })} />
      );
    }
    return error
      ? <Notice tone="error">{error}</Notice>
      : <Loading label={t("campaign.loading")} />;
  }

  async function cancel() {
    try {
      const r = await api.cancelCampaign(cid);
      setNote(t("campaign.cancelled", { count: r.cancelled }));
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }

  async function retry(pos: number) {
    try {
      await api.retryCampaignStop(cid, pos);
      setNote(t("campaign.retried"));
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }

  return (
    <CampaignView c={c} may={atLeast(role, "operator")} note={note} error={error}
                  onCancel={() => void cancel()} onRetry={(pos) => void retry(pos)} />
  );
}

function CampaignView({
  c,
  may,
  note,
  error,
  onCancel,
  onRetry,
}: {
  c: Campaign;
  may: boolean;
  note: string;
  error: string;
  onCancel: () => void;
  onRetry: (pos: number) => void;
}) {
  const { t } = useTranslation();
  const answer = useAnswer(c);
  const totals = useTally(c.planned, c);
  const bench = c.bench_name ?? c.bench_id;
  const build = c.build_ref ? t("campaign.build", { build: campaignBuildName(c) }) : t("campaign.installed");
  const origin = c.origin && c.origin !== "manual"
    ? t(`campaign.origin.${c.origin}` as "campaign.origin.nightly", { when: ago(c.created_at) })
    : t("campaign.startedBy", { who: c.requested_by, when: ago(c.created_at) });

  return (
    <section>
      <PageHead
        back={{ to: `/benches/${c.bench_id}`, label: t("campaign.back", { bench }) }}
        crumbs={(() => {
          const up = trailCrumbs(t, c.trail, "build");
          return up.length ? [...up, { label: t("builds.campaignName", { id: c.id }) }] : undefined;
        })()}
        title={t("campaign.title", { bench })}
        answer={<CampaignAnswer c={c} />}
        bad={answer.bad}
        after={
          <>
            <p>{capital(build)}, {origin}.</p>
            {c.reason && <p>{t("campaign.why", { reason: c.reason })}</p>}
            {c.build_ref && c.build_ref !== campaignBuildName(c) && (
              <details className="text-sm text-slate-500">
                <summary className="cursor-pointer">{t("campaign.imageDetails")}</summary>
                <span className="break-all font-mono">{c.build_ref}</span>
              </details>
            )}
          </>
        }
        actions={may && c.status === "running"
          ? <button type="button" className="btn-quiet" onClick={onCancel}>{t("campaign.cancel")}</button>
          : undefined}
      />

      <Counts testId="campaign-totals" items={totals} />
      <p className="mt-4 text-sm">
        <Link className="underline" to={`/reports?campaign=${c.id}`}>{t("campaign.report")}</Link>
      </p>
      {note && <Notice>{note}</Notice>}
      {error && <Notice tone="error">{error}</Notice>}

      <Section title={t("campaign.routeHeading")}>
        <ol aria-label={t("campaign.routeLabel")}>
          {c.stops.map((s, i) => (
            <Stop
              key={s.pos}
              s={s}
              current={c.current === s.pos}
              last={i === c.stops.length - 1}
              may={may}
              onRetry={() => onRetry(s.pos)}
            />
          ))}
        </ol>
      </Section>
    </section>
  );
}

const capital = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

function useAnswer(c: Campaign): { text: string; bad: boolean } {
  const { t } = useTranslation();
  const cur = c.current !== null ? c.stops.find((s) => s.pos === c.current) : undefined;
  if (c.status === "green") return { text: t("campaign.answerGreen"), bad: false };
  if (c.status === "running") {
    return {
      text: cur
        ? t("campaign.answerRunning", { n: cur.pos + 1, of: c.stops.length, label: cur.label || cur.profile })
        : t("campaign.answerWaiting"),
      bad: false,
    };
  }
  if (c.status === "cancelled") return { text: t("campaign.answerCancelled"), bad: false };
  if (onlyKnown(c)) return { text: t("known.onlyKnown", { n: c.known }), bad: false };
  const fresh = newFailures(c);
  const text = fresh
    ? t("campaign.answerFailed", { count: fresh })
    : c.lost
      ? t("campaign.answerLost")
      : t("campaign.answerNotAllRan");
  return { text: c.known ? `${text} ${t("known.also", { n: c.known })}` : text, bad: true };
}

/** The campaign's answer, in one sentence. */
export function CampaignAnswer({ c }: { c: Campaign }) {
  return <span data-testid="campaign-answer">{useAnswer(c).text}</span>;
}

type Tally = Pick<CampaignStop, "ran" | "passed" | "failed" | "xfailed" | "skipped">;

/** Planned against ran, and what the cases that ran came to. Shared with the
 *  campaigns list so both say the numbers in the same words. */
export function useTally(planned: number | null, x: Tally): Count[] {
  const { t } = useTranslation();
  const items: (Count | null)[] = [
    planned !== null ? { n: planned, label: t("campaign.planned", { count: planned }), tone: "plain" } : null,
    { n: x.ran, label: t("campaign.ran", { count: x.ran }), tone: "plain" },
    { n: x.passed, label: t("campaign.passed", { count: x.passed }), tone: "good" },
    x.xfailed ? { n: x.xfailed, label: t("campaign.expected", { count: x.xfailed }), tone: "warn" } : null,
    x.failed ? { n: x.failed, label: t("campaign.failed", { count: x.failed }), tone: "bad" } : null,
    x.skipped ? { n: x.skipped, label: t("campaign.notRun", { count: x.skipped }), tone: "off" } : null,
  ];
  return items.filter((i): i is Count => i !== null);
}

function Stop({
  s,
  current,
  last,
  may,
  onRetry,
}: {
  s: CampaignStop;
  current: boolean;
  last: boolean;
  may: boolean;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  const tally = useTally(s.planned, s);
  const finished = !ACTIVE.includes(s.state);
  const short = s.planned !== null && s.ran < s.planned;
  const good = s.state === "done" && s.failed === 0 && s.skipped === 0 && !short;
  const tone: Tone = current ? "live" : good ? "good" : finished ? "bad" : "off";
  const marker = {
    live: "border-sky-600 bg-sky-100 text-sky-900 dark:bg-sky-900 dark:text-sky-100",
    good: "border-success-600 bg-success-100 text-success-900 dark:bg-success-900 dark:text-success-100",
    bad: "border-red-600 bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-200",
  }[tone as "live" | "good" | "bad"] ??
    "border-slate-300 bg-white text-slate-600 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-300";
  return (
    <li className="relative pb-6 pl-11" aria-current={current ? "step" : undefined}>
      {!last && (
        <span className="absolute left-[13px] top-8 h-[calc(100%-2rem)] w-0.5 bg-slate-300 dark:bg-slate-700" aria-hidden="true" />
      )}
      <span
        className={`absolute left-0 top-0 flex h-7 w-7 items-center justify-center rounded-full border-2 text-sm font-semibold ${marker}`}
        aria-hidden="true"
      >
        {s.pos + 1}
      </span>
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <span className="text-base font-semibold text-slate-900 dark:text-slate-100">{s.label || s.profile}</span>
        <Status tone={tone}>{t(`campaign.state.${s.state}` as "campaign.state.done")}</Status>
        {s.attempts > 1 && (
          <span className="text-sm text-slate-500">{t("campaign.attempt", { n: s.attempts })}</span>
        )}
      </div>
      <RunAboutView about={s.about} />
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
        {tally.map((x) => x.label).join(", ")}
      </p>
      {/* "11 planned, 0 ran, 0 passed" over a run that failed two checks and
          then lost its display (run40) read as "nothing happened here". */}
      {(s.known ?? []).length > 0 && (
        <p className="text-sm text-amber-800 dark:text-amber-300" data-testid="stop-known">
          {t("known.onStop", { list: (s.known ?? []).join(", ") })}
        </p>
      )}
      {s.lost && (
        <p className="text-sm font-medium text-red-800 dark:text-red-300" data-testid="stop-lost">
          {t("campaign.stopLost", { n: s.failed_checks ?? 0 })}
        </p>
      )}
      {s.detail && s.state !== "done" && <p className="text-sm text-slate-500">{s.detail}</p>}
      <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
        {s.run_id !== null && (
          <Link className="underline" to={`/runs/${s.run_id}`}>
            {t("campaign.openRun")}
          </Link>
        )}
        {may && finished && (
          <button type="button" className="btn-quiet" onClick={onRetry}>
            {t("campaign.retry")}
          </button>
        )}
      </div>
    </li>
  );
}
